import {
  FULL_DATE_TO_STRING_OPTION,
  MONEY_SYMBOL,
  PERCENT_SYMBOL,
  SLASH_SYMBOL,
  COLON_SYMBOL,
  SEPARATOR_SYMBOL,
  ELLIPSIS,
  HYPHEN,
  TRAILING_ZERO,
} from '../constants';
import { TTranslationPayload } from '../types';

type TFormatterValue = number | TTranslationPayload;

export const capitalize = (value: string): string => {
  if (typeof value !== 'string') return '';

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const appendSymbol = (value: TFormatterValue, symbol: string): string => `${value} ${symbol}`;

const prependedSymbol = (symbol: string, value: TFormatterValue, indent: boolean = true): string =>
  [symbol, value].join(indent ? ' ' : '');

export const percentFormatter = (value: TFormatterValue | null): string =>
  value !== null ? appendSymbol(value, PERCENT_SYMBOL) : HYPHEN;

export const moneyFormatter = (value: TFormatterValue, indent: boolean = true): string =>
  prependedSymbol(MONEY_SYMBOL, value, indent);

export const slashFormatter = (value: TFormatterValue): string => appendSymbol(value, SLASH_SYMBOL);

export const colonFormatter = (value: TFormatterValue): string => appendSymbol(value, COLON_SYMBOL);

export const routeOneAppFormatter = (value: string | null): string | null =>
  value !== null && value?.trim().endsWith(TRAILING_ZERO) ? value?.trim().slice(0, -2) : value?.trim() ?? value;

export const vinFormatter = (value: string): string => (value !== '' ? value.slice(0, 4) + ELLIPSIS : HYPHEN);

export const separatorFormatter = (value: TFormatterValue): string => prependedSymbol(`${SEPARATOR_SYMBOL} `, value);

export const thousandFormatter = (value: number, options?: Intl.NumberFormatOptions): string =>
  Intl.NumberFormat('en-US', options).format(value);

export const thousandFormatterOrHyphen = (value: number | null): string =>
  value !== null ? thousandFormatter(value) : HYPHEN;

export const valueOrHyphen = (value: number | string | null): string =>
  value !== null && value !== '' ? value.toString() : HYPHEN;

export const costFormatter = (value: number | null, indent: boolean = true): string =>
  value !== null ? moneyFormatter(thousandFormatter(value), indent) : HYPHEN;

export const reportCostFormatter = (value: number): string =>
  moneyFormatter(thousandFormatter(Number(value.toFixed(2))));

const toDate = (value: string, option?: Record<string, string>): string =>
  Intl.DateTimeFormat('en-US', option).format(new Date(value));

export const fullDateFormatter = (value: string): string => toDate(value, FULL_DATE_TO_STRING_OPTION);

export const numericDateFormatter = (value: string): string => toDate(value);

export const getExtraCashConversionValue = (value: number): string => {
  switch (true) {
    case value >= 0 && value <= 500:
      return '$0';
    case value >= 500 && value <= 1000:
      return 'up to $1,000';
    case value >= 1001 && value <= 2000:
      return 'up to $2,000';
    case value >= 2001 && value <= 3000:
      return 'up to $3,000';
    case value >= 3001 && value <= 4000:
      return 'up to $4,000';
    case value >= 4001 && value <= 5000:
      return 'up to $5,000';
    case value > 5000:
      return 'more than $5,000';
    default:
      return thousandFormatter(value);
  }
};

export const getObjectProperty = (object: Record<string, any>, path: string): Record<string, any> | any => {
  if (path === undefined || path === null) {
    return object;
  }

  const parts = path.split('.');

  return parts.reduce((acc, key) => acc?.[key], object);
};
